/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useLocation, useNavigate } from "react-router-dom";
import { hasNavigatedRestaurant, navigatedRestaurantGroupId, navigatedRestaurantGroupName, useNavigatedRestaurantFilter } from "../../hooks/useNavigatedRestaurantFilter";
import { invalidateAuditLogQueries } from "../../utils/invalidateAuditLogQueries";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Select,
  Typography,
} from "@mui/material";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CachedIcon from "@mui/icons-material/Cached";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../user_management/styles";
import { FilterContainer } from "../../components/filter_container";
import { StyledFilterCancel, StyledFormControl } from "../dashboard/styles";
import { StyledInputLabel } from "../restaurant/styles.jsx";
import { selectBaseStyles } from "../reports/styles";
import DropdownSearchBar from "../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { StyledMenuItem } from "../../components/styled_menu_item";
import { ResetButton } from "../../components/reset_button";
import { UpdateButton } from "../../components/update_button";
import { Loader } from "../../components/loader";
import { IndexTable } from "../../components/index_table";
import { LoadingSpinner } from "../../components/loading_spinner";
import { StyledSearchBar } from "../employee/styles.js";
import { AbilityContext } from "../../contexts/abilityContext";
import { useNotificationContext } from "../../contexts/notificationContext";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { mealPeriodMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/mealPeriodMutationKeys";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { Query_Keys } from "../../lib/queryKeys";
import { PerPage } from "../../constants/perPage";
import { strictCanScope } from "../../utils/caslStrict";
import { checkAdmin } from "../../utils/checkAdmin";
import camelize from "../../utils/camelize";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { useDebounce } from "../../utils/useDebounce";
import { formatDate } from "../../utils/formatDate";
import { formatHelper } from "../../utils/formatHelper";
import { hasSingleItem } from "../../utils/hasSingleItem";
import { getSingleItemValue } from "../../utils/getSingleItemValue";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery";
import {
  FetchButtonContainer,
  MealPeriodPage,
  actionSectionStyle,
  fetchButtonStyle,
  filterContainerStyle,
  filterSectionStyle,
  menuPropStyle,
  pageTitleStyle,
  titleSectionStyle,
  typographyStyle,
} from "./styles";

const EMPTY_FILTERS = {
  restaurant_group_id: '',
  restaurant_sub_group_id: '',
  restaurant_ids: [],
}

const TABLE_COLUMNS = [
  { key: 'id', sortable: true },
  { key: 'name', sortable: true },
  { key: 'restaurant', sortable: true },
  { key: 'guid', sortable: true },
  { key: 'source_of_truth', sortable: true },
  { key: 'created_at', sortable: true },
]

const flattenDropdownPages = (data) => data.pages.flat()

const EMPTY_DROPDOWN_RESULT = []

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export const MealPeriods = () => {
  const location = useLocation()
  const navigate = useNavigate()
  // Captured once and then cleared off the history entry: React Router keeps location.state across
  // reloads and back/forward, so reading it every render would re-apply a filter the operator has
  // since cleared, and would keep the auto-apply effects below suppressed for the whole visit.
  const [navigatedRestaurant] = useState(() => location?.state?.restaurant)
  const navigatedGroupIdFromState = navigatedRestaurantGroupId(navigatedRestaurant)
  const navigatedGroupNameFromState = navigatedRestaurantGroupName(navigatedRestaurant)
  const singleRestaurantGroupAutoApplied = useRef(false)
  const singleRestaurantAutoApplied = useRef(false)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [search, setSearch] = useState('')
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState('')
  const [restaurantSearch, setRestaurantSearch] = useState('')
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([])
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const { ability } = useContext(AbilityContext)
  const { admin } = useSelector((state) => state.admin)
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const showNotification = useNotificationContext()
  const queryClient = useQueryClient()
  const debouncedSearchTerm = useDebounce(search, 300)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)

  const hasRestaurantGroupSelected = Boolean(filters?.restaurant_group_id)

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })

    return response?.data?.map(normalizeRestaurantGroup) || []
  }

  const {
    data: restaurantGroups = EMPTY_DROPDOWN_RESULT,
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: 'meal_periods',
    }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantSubGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_sub_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
        restaurant_group_id: filters?.restaurant_group_id,
      },
    })

    return response?.data?.map((subGroup) => ({
      id: subGroup?.value,
      name: subGroup?.label,
    })) || []
  }

  const {
    data: restaurantSubGroups = EMPTY_DROPDOWN_RESULT,
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
      source: 'meal_periods',
    }),
    queryFn: fetchRestaurantSubGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    enabled: hasRestaurantGroupSelected,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurant_group_id || undefined,
        restaurant_sub_group_id: filters?.restaurant_sub_group_id || undefined,
      },
    })

    return response?.data?.map(normalizeRestaurant) || []
  }

  const {
    data: restaurants = EMPTY_DROPDOWN_RESULT,
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_sub_group_id: filters?.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
      source: 'meal_periods',
    }),
    queryFn: fetchRestaurantList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const fetchMealPeriods = async () => {
    const response = await api.get('/v1/admin/meal_periods', {
      params: {
        ...appliedFilters,
        search: debouncedSearchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: page,
        per_page: itemsPerPage,
      },
    })

    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const hasAppliedSelection = Boolean(appliedFilters?.restaurant_group_id || appliedFilters?.restaurant_ids?.length > 0)

  const { data: mealPeriods, isFetching, isError } = useQuery({
    queryKey: Query_Keys.meal_periods({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
      search: debouncedSearchTerm,
    }),
    queryFn: fetchMealPeriods,
    staleTime: 10 * 60 * 1000,
    enabled: hasAppliedSelection,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'))
  }, [restaurantGroups])

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantSubGroups, 'id'))
  }, [restaurantSubGroups])

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'))
  }, [restaurants])

  // The selected restaurants the operator is actually allowed to import for. Derived rather than
  // held in state: the ability is filled in asynchronously by the admin fetch, so a value computed
  // once at selection time can still be empty by the time the button is pressed.
  const permittedRestaurantIds = useMemo(() => {
    const selectedRestaurantIds = filters?.restaurant_ids || []

    if (superAdmin?.role === 'admin') return selectedRestaurantIds

    return selectedRestaurantIds
      .filter((id) => strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${id}`))
  }, [ability, superAdmin?.role, filters?.restaurant_ids])

  const fetchPermission = permittedRestaurantIds.length > 0

  // A navigated prefill can only land if it carried an id, or a group that lets the restaurant
  // dropdown load and resolve the name. Without either, the page's own auto-apply must stay live --
  // otherwise the operator lands on a page less pre-filled than if they had opened it directly.
  const navigatedPrefillCanLand = Boolean(
    navigatedRestaurant?.id || (hasNavigatedRestaurant(navigatedRestaurant) && (navigatedGroupIdFromState || filters?.restaurant_group_id))
  )

  useEffect(() => {
    if (!location?.state?.restaurant) {
      return
    }

    navigate(location.pathname, { replace: true, state: null })
  }, [])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups)
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants)

  useEffect(() => {
    if (navigatedGroupIdFromState || filters?.restaurant_group_id || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    singleRestaurantGroupAutoApplied.current = true
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: getSingleItemValue(restaurantGroups)?.label,
    }))
    setFiltersChanged(true)
  }, [filters?.restaurant_group_id, hasSingleAvailableRestaurantGroup, navigatedGroupIdFromState, restaurantGroups])

  useEffect(() => {
    // restaurantFetching gates this on settled data: the dropdown query keeps the previous
    // group's options as placeholder data while refetching, and auto-applying against those
    // carries a restaurant from the old group into the new filter.
    if (
      navigatedPrefillCanLand
      || filters?.restaurant_ids?.length > 0
      || singleRestaurantAutoApplied.current
      || !hasSingleAvailableRestaurant
      || restaurantFetching
    ) {
      return
    }

    const singleRestaurant = getSingleItemValue(restaurants)

    singleRestaurantAutoApplied.current = true
    setFilters((currentFilters) => ({ ...currentFilters, restaurant_ids: [singleRestaurant?.id] }))
    setFiltersChanged(true)
  }, [filters?.restaurant_ids, hasSingleAvailableRestaurant, navigatedPrefillCanLand, restaurants, restaurantFetching])

  const handleRestaurantGroupChange = (event) => {
    singleRestaurantAutoApplied.current = false
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: event.target.value,
      restaurant_sub_group_id: '',
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantGroupClear = () => {
    singleRestaurantGroupAutoApplied.current = true
    singleRestaurantAutoApplied.current = true
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: '',
      restaurant_sub_group_id: '',
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupChange = (event) => {
    singleRestaurantAutoApplied.current = false
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_sub_group_id: event.target.value,
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupClear = () => {
    singleRestaurantAutoApplied.current = true
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_sub_group_id: '',
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantChange = (event) => {
    const selectedRestaurantIds = event.target.value || []

    setFilters((currentFilters) => ({ ...currentFilters, restaurant_ids: selectedRestaurantIds }))
    setFiltersChanged(true)
  }

  const handleRestaurantClear = () => {
    singleRestaurantAutoApplied.current = true
    setFilters((currentFilters) => ({ ...currentFilters, restaurant_ids: [] }))
    setFiltersChanged(true)
  }

  const applySelectedFilters = (nextFilters) => {
    setAppliedFilters(nextFilters)
    setFiltersChanged(false)
    setItemsPerPage(25)
    setPage(1)
  }

  useNavigatedRestaurantFilter({
    navigatedRestaurant,
    emptyFilters: EMPTY_FILTERS,
    restaurantOptionsCache,
    restaurantSearch,
    setFilters,
    setRestaurantSearch,
    applySelectedFilters,
  })

  const handleUpdate = () => {
    if (!filters?.restaurant_group_id && filters?.restaurant_ids?.length === 0) {
      showNotification('Please select a restaurant group or restaurant', 'error')
      return
    }

    applySelectedFilters(filters)
  }

  const handleReset = () => {
    singleRestaurantGroupAutoApplied.current = true
    singleRestaurantAutoApplied.current = true
    setFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setFiltersChanged(false)
    setRestaurantGroupSearch('')
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setSearch('')
    setSortBy('created_at')
    setSortOrder('desc')
    setItemsPerPage(25)
    setPage(1)
  }

  const handleSearch = (searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
  }

  // Falls back to the name the navigation carried: a group beyond the first dropdown page is not in
  // the cache yet, and without this the chip renders blank even though the filter applied.
  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((group) => `${group?.label}` === `${id}`)
    if (restaurantGroup?.value) {
      return restaurantGroup.value
    }

    return `${navigatedGroupIdFromState}` === `${id}` ? navigatedGroupNameFromState || '' : ''
  }

  const getRestaurantSubGroupName = (id) => {
    const restaurantSubGroup = restaurantSubGroupOptionsCache?.find((subGroup) => `${subGroup?.id}` === `${id}`)
    return restaurantSubGroup?.name || ''
  }

  const getRestaurantName = (id) => {
    const restaurant = restaurantOptionsCache?.find((res) => `${res?.id}` === `${id}`)
    return restaurant?.name || ''
  }

  const getRestaurantDisplayValue = () => {
    const selectedRestaurantIds = filters?.restaurant_ids || []

    if (selectedRestaurantIds.length === 0) return ''
    if (selectedRestaurantIds.length === 1) return getRestaurantName(selectedRestaurantIds[0])

    return `${selectedRestaurantIds.length} Restaurants Selected`
  }

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${filters?.restaurant_group_id}`
  )
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], 'label')
      : restaurantGroups

  const selectedRestaurantSubGroupOption = restaurantSubGroupOptionsCache?.find(
    (subGroup) => `${subGroup?.id}` === `${filters?.restaurant_sub_group_id}`
  )
  const displayedRestaurantSubGroups = restaurantSubGroupSearch.trim()
    ? restaurantSubGroups
    : selectedRestaurantSubGroupOption
      ? mergeUniqueByKey(restaurantSubGroups, [selectedRestaurantSubGroupOption], 'id')
      : restaurantSubGroups

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) => (
    filters?.restaurant_ids?.some((id) => `${restaurant?.id}` === `${id}`)
  ))
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, 'id')

  const { mutateAsync: fetchMealPeriodsMutation } = useOtMutation({
    mutationFn: async (restaurantId) => await api.post('/v1/admin/meal_periods/fetch_meal_periods', { restaurant_id: restaurantId }),
    invalidateKeys: mealPeriodMutationKeyGroups.fetch,
    successMessage: ({ variables: restaurantId }) => `Meal periods imported successfully for ${getRestaurantName(restaurantId)}`,
    errorMessage: ({ variables: restaurantId }) => `Failed to import meal periods for ${getRestaurantName(restaurantId)}`,
    onSettled: () => {
      invalidateAuditLogQueries(queryClient);
    },
  })

  const handleManualImport = async () => {
    const selectedRestaurantIds = filters?.restaurant_ids || []

    if (selectedRestaurantIds.length === 0) {
      showNotification('Please select at least one restaurant', 'error')
      return
    }

    // The button is enabled when the operator may import for at least one selected restaurant,
    // so a mixed selection is normal. Report what is being left out instead of dropping it
    // silently.
    const skippedCount = selectedRestaurantIds.length - permittedRestaurantIds.length

    if (skippedCount > 0) {
      showNotification(
        `Skipped ${skippedCount} of ${selectedRestaurantIds.length} restaurants — you do not have import permission for them`,
        'warning'
      )
    }

    if (permittedRestaurantIds.length === 0) return

    setIsLoading(true)

    let importedAny = false

    try {
      for (const restaurantId of permittedRestaurantIds) {
        // Paced one request at a time: the endpoint calls Toast inline, so this must not fan out.
        await delay(3500)

        try {
          await fetchMealPeriodsMutation(restaurantId)
          importedAny = true
        } catch {
          // Reported by useOtMutation onError. Caught per restaurant so one failure does not
          // abandon the restaurants still queued behind it.
        }
      }
    } finally {
      setIsLoading(false)
    }

    // Show what was just imported. Without this, importing before pressing Update leaves the
    // "select a filter" alert on screen despite a successful run. Guarded so an operator who has
    // already applied filters and paged into the results is not thrown back to page one.
    if (importedAny && (!hasAppliedSelection || filtersChanged)) {
      applySelectedFilters(filters)
    }
  }

  const handleExportCsv = async () => {
    if (csvLoading) return

    try {
      setCsvLoading(true)

      const query = buildQueryParams({
        ...appliedFilters,
        search: debouncedSearchTerm,
        sort_by: sortBy,
        sort_order: sortOrder,
      })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=meal_periods&${query}`

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV',
      })

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `meal_periods_${dayjs().format('YYYY-MM-DD')}.csv`
      a.click()

      window.URL.revokeObjectURL(downloadUrl)
      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      console.error('CSV download error:', error)
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }

  const columns = TABLE_COLUMNS?.map((column) => ({
    key: column?.key,
    label: camelize(column?.key),
    sortable: column?.sortable,
  }))

  const TableRows = (data) => {
    return data?.map((record) => ({
      id: record?.id,
      name: record?.name || '-',
      restaurant: record?.restaurant?.name || '-',
      guid: record?.guid || '-',
      source_of_truth: formatHelper(record?.source_of_truth) || '-',
      created_at: formatDate(record?.created_at, record?.time_zone_abbr),
    }))
  }

  const rows = TableRows(mealPeriods?.data)
  const hasSearchTerm = !!debouncedSearchTerm

  const handleSortChange = (columnKey, direction) => {
    setSortBy(columnKey)
    setSortOrder(direction)
    setPage(1)
  }

  return (
    <MealPeriodPage>
      {/* Bread crumbs */}
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Restaurant Management</StyledLink>
          <StyledLink color={"#515152"}>Meal Period</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      {/* Filters */}
      <FilterContainer>
        <Box sx={filterContainerStyle}>
          <Box sx={filterSectionStyle}>
            <Typography variant="body2" sx={typographyStyle}>
              View
            </Typography>

            {/* Restaurant group filter */}
            <StyledFormControl size="small">
              <StyledInputLabel>Restaurant Group</StyledInputLabel>
              <Select
                data-testid="meal-periods-restaurant-group-filter"
                size="small"
                label="Restaurant Group"
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantGroupMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "160px" },
                  width: { xs: "100%", sm: "160px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                value={filters?.restaurant_group_id || ""}
                renderValue={() => getRestaurantGroupName(filters?.restaurant_group_id)}
                endAdornment={
                  filters?.restaurant_group_id ? (
                    <StyledFilterCancel position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        onClick={handleRestaurantGroupClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </StyledFilterCancel>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                {displayedRestaurantGroups.map((res) => (
                  <StyledMenuItem
                    key={res?.label}
                    value={res?.label}
                  >
                    {
                      `${filters?.restaurant_group_id}` === `${res?.label}` ? <SelectedMenuItem data={res?.value} /> : res?.value
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </StyledFormControl>

            {/* Restaurant sub group filter */}
            <StyledFormControl size="small" disabled={!hasRestaurantGroupSelected}>
              <StyledInputLabel>Restaurant Sub Group</StyledInputLabel>
              <Select
                data-testid="meal-periods-restaurant-sub-group-filter"
                size="small"
                label="Restaurant Sub Group"
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantSubGroupMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "215px" },
                  width: { xs: "100%", sm: "215px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantSubGroupChange}
                onClose={() => setTimeout(() => setRestaurantSubGroupSearch(''), 1000)}
                value={filters?.restaurant_sub_group_id || ""}
                renderValue={() => getRestaurantSubGroupName(filters?.restaurant_sub_group_id)}
                endAdornment={
                  filters?.restaurant_sub_group_id ? (
                    <StyledFilterCancel position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        onClick={handleRestaurantSubGroupClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </StyledFilterCancel>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSubGroupSearch} placeholder={'Search restaurant sub group'} isFetching={restaurantSubGroupFetching} />
                {displayedRestaurantSubGroups.map((res) => (
                  <StyledMenuItem
                    key={res?.id}
                    value={res?.id}
                  >
                    {
                      `${filters?.restaurant_sub_group_id}` === `${res?.id}` ? <SelectedMenuItem data={res?.name} /> : res?.name
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantSubGroupsPage}
                  targetRef={restaurantSubGroupLoaderRef}
                />
              </Select>
            </StyledFormControl>

            {/* Restaurant filter */}
            <StyledFormControl size="small">
              <StyledInputLabel>Restaurant</StyledInputLabel>
              <Select
                data-testid="meal-periods-restaurant-filter"
                size="small"
                label="Restaurant"
                multiple
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "160px" },
                  width: { xs: "100%", sm: "160px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                value={filters?.restaurant_ids || []}
                renderValue={() => getRestaurantDisplayValue()}
                endAdornment={
                  filters?.restaurant_ids?.length > 0 ? (
                    <StyledFilterCancel position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </StyledFilterCancel>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                {displayedRestaurants.map((res) => (
                  <StyledMenuItem
                    key={res?.id}
                    value={res?.id}
                  >
                    {
                      filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </StyledFormControl>
          </Box>

          <Box sx={actionSectionStyle}>
            <ResetButton
              onClick={handleReset}
              status={isFetching}
            />

            <UpdateButton
              onClick={handleUpdate}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </FilterContainer>

      {/* Page title and search */}
      <Box sx={titleSectionStyle}>
        <Typography variant="body2" sx={pageTitleStyle} data-testid="page-title">
          Meal Period
        </Typography>

        <StyledSearchBar
          value={search}
          placeholder="Search name"
          disabled={!hasAppliedSelection}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
          onChange={(event) => handleSearch(event.target.value)}
          autoComplete="off"
          inputProps={{ "data-testid": "meal-periods-search-input" }}
          sx={{ mt: '8px' }}
        />
      </Box>

      <Divider sx={{ borderStyle: 'solid', borderWidth: '1px', marginBottom: '25px' }} />

      {/* Manual import */}
      <FetchButtonContainer>
        <Button
          data-testid="meal-periods-manual-import-btn"
          sx={fetchButtonStyle}
          variant="contained"
          onClick={handleManualImport}
          disabled={!fetchPermission || isLoading}
        >
          {isLoading ? <LoadingSpinner isLoading={true} /> : <CachedIcon />}
        </Button>
      </FetchButtonContainer>

      {/* Index table */}
      {isFetching && !hasSearchTerm ? (
        <Loader />
      ) : isError ? (
        <div>
          <Alert
            data-testid="meal-periods-error-message"
            severity="error"
          >
            Could not load meal periods. Please retry.
          </Alert>
        </div>
      ) : (hasAppliedSelection && mealPeriods?.data) ? (
        <IndexTable
          columns={columns}
          data={rows}
          tableId="meal-periods"
          type="entries"
          report={false}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          pageCount={mealPeriods?.pageCount}
          searchLoading={isFetching && hasSearchTerm}
          hasSearchTerm={hasSearchTerm}
          showDownloadButton={true}
          disableDownloadButton={mealPeriods?.data?.length === 0 || csvLoading}
          handleExportCsv={handleExportCsv}
          csvLoading={csvLoading}
          serverSort={true}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
      ) : (
        <div>
          <Alert
            data-testid="meal-periods-alert-message"
            severity="info"
          >
            Select a restaurant group or restaurant
          </Alert>
        </div>
      )}
    </MealPeriodPage>
  )
}
